import React, { useContext, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";

import PageLayout from "../components/Layout/PageLayout";
import { LoadingOverlay } from "../components/Navigation/LoadingOverlay";
import { FleetContext, FleetSettingsState, GeofencesState, GeofencePackageState } from "../context";
import { Fleet, VehicleDetail, NewFleet, FleetSettings, Geofences, Sidewalks } from "../views";
import SwapComponent from "../views/Fleet/SwapComponent";
import ExportJSON from "../views/Fleet/ExportJSON";
import RemoveComponent from "../views/Fleet/RemoveComponent";
import GeofencePackageCreationModal from "../views/Fleet/GeofencePackageCreationModal";
import PermissionedRoute from "./PermissionedRoute";
import { Ota } from "../views/OTA";
import Permissions from "../permissions";
import { SidewalkExportOptions, GeofencesExportOptions } from "../constants";
import { accessTokenCookieKey, getCookies, refreshTokenCookieKey } from "../API/cookies";

export const FleetRouter = () => {
  const { fetchFleets, activeFleet } = useContext(FleetContext);
  const location = useLocation();
  const background = location?.state?.background || location;

  useEffect(() => {
    if (!activeFleet) {
      fetchFleets();
    }
  }, [activeFleet]);

  if (!getCookies(accessTokenCookieKey) && !getCookies(refreshTokenCookieKey))
    return <Redirect to={{ pathname: "/logout", state: { from: location.pathname } }} />;
  if (!activeFleet) return <LoadingOverlay />;
  return (
    <>
      <Switch location={background || location}>
        <PermissionedRoute
          permission={[Permissions.readFleets, Permissions.writeFleets, Permissions.writeRatePlans]}
          path="/fleets/new"
        >
          <PageLayout>
            <NewFleet />
          </PageLayout>
        </PermissionedRoute>
        <Route path="/fleets/:fleetId/">
          <PermissionedRoute
            permission={[Permissions.readVehicles, Permissions.writeVehicles]}
            exact
            path="/fleets/:fleetId/vehicles/:vehicleId"
          >
            <PageLayout>
              <VehicleDetail />
            </PageLayout>
          </PermissionedRoute>
          <PermissionedRoute permission={Permissions.readFleets} path="/fleets/:fleetId/settings">
            <PageLayout>
              <FleetSettingsState>
                <FleetSettings />
              </FleetSettingsState>
            </PageLayout>
          </PermissionedRoute>
          <PermissionedRoute
            permission={[
              Permissions.feature.otaPage,
              Permissions.readFirmware,
              Permissions.writeFirmware,
            ]}
            exact
            path="/fleets/:fleetId/ota"
          >
            <PageLayout>
              <Ota />
            </PageLayout>
          </PermissionedRoute>
          <PermissionedRoute
            permission={[
              Permissions.writeGeofences,
              Permissions.feature.geofenceCreation,
              Permissions.readParkingIncentives,
              Permissions.writeParkingIncentives,
            ]}
            exact
            path="/fleets/:fleetId/geofences"
          >
            <PageLayout>
              <FleetSettingsState>
                <GeofencesState>
                  <Geofences />
                </GeofencesState>
              </FleetSettingsState>
            </PageLayout>
          </PermissionedRoute>
          <PermissionedRoute
            permission={[
              Permissions.writeGeofences,
              Permissions.feature.geofenceCreation,
              Permissions.readParkingIncentives,
              Permissions.writeParkingIncentives,
            ]}
            exact
            path="/fleets/:fleetId/geofences/edit"
          >
            <PageLayout>
              <FleetSettingsState>
                <GeofencesState>
                  <Geofences isEditMode />
                </GeofencesState>
              </FleetSettingsState>
            </PageLayout>
          </PermissionedRoute>
          <PermissionedRoute
            permission={Permissions.feature.sidewalks}
            exact
            path="/fleets/:fleetId/sidewalks"
          >
            <PageLayout>
              <FleetSettingsState>
                <GeofencesState sidewalksMode>
                  <Sidewalks />
                </GeofencesState>
              </FleetSettingsState>
            </PageLayout>
          </PermissionedRoute>
          <PermissionedRoute permission={Permissions.readFleets} exact path="/fleets/:fleetId/">
            <PageLayout>
              <Fleet />
            </PageLayout>
          </PermissionedRoute>
        </Route>
        <Route path="/fleets">
          <Redirect to={`/fleets/${activeFleet.uuid4}/`} />
        </Route>
      </Switch>
      {/* modals */}
      {background && (
        <Switch>
          <PermissionedRoute
            permission={Permissions.basicWrite}
            path="/fleets/:fleetId/vehicles/:vehicleId/swap-serialized-component"
          >
            <SwapComponent />
          </PermissionedRoute>
          <PermissionedRoute
            permission={Permissions.basicWrite}
            path="/fleets/:fleetId/vehicles/:vehicleId/remove-serialized-component"
          >
            <RemoveComponent />
          </PermissionedRoute>
          <PermissionedRoute
            permission={[
              Permissions.writeGeofences,
              Permissions.feature.geofenceCreation,
              Permissions.readParkingIncentives,
              Permissions.writeParkingIncentives,
            ]}
            path="/fleets/:fleetId/geofences/edit/export"
          >
            <FleetSettingsState>
              <ExportJSON fileTypes={Object.values(GeofencesExportOptions)} />
            </FleetSettingsState>
          </PermissionedRoute>

          <PermissionedRoute
            permission={[
              Permissions.writeGeofences,
              Permissions.feature.geofenceCreation,
              Permissions.readParkingIncentives,
              Permissions.writeParkingIncentives,
            ]}
            path="/fleets/:fleetId/geofences/edit/create-package"
          >
            <GeofencePackageState activeFleet={activeFleet} isPreview={false}>
              <GeofencePackageCreationModal />
            </GeofencePackageState>
          </PermissionedRoute>

          <PermissionedRoute
            permission={[
              Permissions.writeGeofences,
              Permissions.feature.geofenceCreation,
              Permissions.readParkingIncentives,
              Permissions.writeParkingIncentives,
            ]}
            path="/fleets/:fleetId/geofences/edit/create-preview-package"
          >
            <GeofencePackageState isPreview activeFleet={activeFleet}>
              <GeofencePackageCreationModal />
            </GeofencePackageState>
          </PermissionedRoute>

          <PermissionedRoute
            permission={Permissions.feature.sidewalks}
            path="/fleets/:fleetId/sidewalks/export"
          >
            <FleetSettingsState>
              <ExportJSON fileTypes={Object.values(SidewalkExportOptions)} />
            </FleetSettingsState>
          </PermissionedRoute>
        </Switch>
      )}
    </>
  );
};

export default FleetRouter;
